import React, { forwardRef } from 'react';

/**
 * Bombiix master card — Figma-locked container (spec: Bombiix 2026 › Cards).
 * Domain-agnostic compound component; consumers wire their own icons, image,
 * title, meta lines, and tag chips. Styled by `assets/scss/master-design/_card.scss`.
 *
 * Example:
 *   <BombiixCard>
 *     <BombiixCard.Header>
 *       <BombiixCard.Actions>
 *         <li><button className="bombiix-icon-container"><i className="bombiix-icon-edit" /></button></li>
 *       </BombiixCard.Actions>
 *       <BombiixCard.Thumb src="/img/foo.jpg" enlargeOnHover />
 *     </BombiixCard.Header>
 *     <BombiixCard.Body>
 *       <BombiixCard.Title>SMVR-001</BombiixCard.Title>
 *       <BombiixCard.Meta>
 *         <li>Commuter Jacket</li>
 *       </BombiixCard.Meta>
 *     </BombiixCard.Body>
 *     <BombiixCard.Footer>
 *       <BombiixCard.Tag>Product Status</BombiixCard.Tag>
 *     </BombiixCard.Footer>
 *   </BombiixCard>
 */

type DivProps = React.HTMLAttributes<HTMLDivElement>;
type UlProps = React.HTMLAttributes<HTMLUListElement>;
type SpanProps = React.HTMLAttributes<HTMLSpanElement>;
type AnchorProps = React.AnchorHTMLAttributes<HTMLAnchorElement>;

interface ThumbProps extends DivProps {
  src?: string;
  /** Higher-resolution source for the hover-enlarge preview; falls back to `src`. */
  previewSrc?: string;
  alt?: string;
  enlargeOnHover?: boolean;
}

interface TagProps extends SpanProps {
  variant?: 'green' | 'red' | 'amber' | 'blue' | 'purple';
}

const joinClass = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(' ');

const Card = forwardRef<HTMLDivElement, DivProps>(function Card({ className, children, ...rest }, ref) {
  return (
    <div ref={ref} className={joinClass('bombiix-card', className)} {...rest}>
      {children}
    </div>
  );
});

const Header = forwardRef<HTMLDivElement, DivProps>(function Header({ className, children, ...rest }, ref) {
  return (
    <div ref={ref} className={joinClass('bombiix-card__header', className)} {...rest}>
      {children}
    </div>
  );
});

const Actions = forwardRef<HTMLUListElement, UlProps>(function Actions({ className, children, ...rest }, ref) {
  return (
    <ul ref={ref} className={joinClass('bombiix-card__actions', className)} {...rest}>
      {children}
    </ul>
  );
});

/**
 * Card thumbnail, optionally with a hover-enlarged preview.
 *
 * The small thumb renders `src` (a low-resolution list thumbnail). When `previewSrc`
 * is supplied the enlarged preview uses that instead, so the popup shows the
 * full-resolution original rather than an upscaled — and visibly blurry — thumbnail.
 * Without `previewSrc` the preview falls back to `src`, preserving prior behaviour.
 */
const Thumb = forwardRef<HTMLDivElement, ThumbProps>(function Thumb({ className, src, previewSrc, alt, style, enlargeOnHover = false, ...rest }, ref) {
  const mergedStyle: React.CSSProperties = {
    ...(src ? { backgroundImage: `url(${src})` } : {}),
    ...style,
  };
  const thumb = (
    <div ref={ref} role={alt ? 'img' : undefined} aria-label={alt} className={joinClass('bombiix-card__thumb', className)} style={mergedStyle} {...rest} />
  );

  if (!enlargeOnHover) {
    return thumb;
  }

  const enlargedSrc = previewSrc || src;
  const previewStyle: React.CSSProperties = {
    ...mergedStyle,
    ...(enlargedSrc ? { backgroundImage: `url(${enlargedSrc})` } : {}),
  };

  return (
    <div className="bombiix-card__thumb-wrap">
      {thumb}
      <div className="bombiix-card__thumb-preview" style={previewStyle} aria-hidden="true" />
    </div>
  );
});

const Body = forwardRef<HTMLDivElement, DivProps>(function Body({ className, children, ...rest }, ref) {
  return (
    <div ref={ref} className={joinClass('bombiix-card__body', className)} {...rest}>
      {children}
    </div>
  );
});

/**
 * Card title. Renders an anchor only when the caller gives it somewhere to go.
 * It previously fell back to `href="#!"`, which made every title look and
 * behave like a link — clicking a card that had no destination still pushed a
 * history entry and moved the page. Cards that do link (the Partner card) pass
 * `href` and are unaffected.
 */
const Title = forwardRef<HTMLAnchorElement & HTMLSpanElement, AnchorProps>(function Title({ className, children, href, ...rest }, ref) {
  const titleClass = joinClass('bombiix-card__title', className);
  if (!href) {
    return (
      <span
        ref={ref as React.Ref<HTMLSpanElement>}
        className={joinClass(titleClass, 'bombiix-card__title--static')}
        {...(rest as React.HTMLAttributes<HTMLSpanElement>)}
      >
        {children}
      </span>
    );
  }
  return (
    <a ref={ref as React.Ref<HTMLAnchorElement>} href={href} className={titleClass} {...rest}>
      {children}
    </a>
  );
});

const Meta = forwardRef<HTMLUListElement, UlProps>(function Meta({ className, children, ...rest }, ref) {
  return (
    <ul ref={ref} className={joinClass('bombiix-card__meta', className)} {...rest}>
      {children}
    </ul>
  );
});

const Footer = forwardRef<HTMLDivElement, DivProps>(function Footer({ className, children, ...rest }, ref) {
  return (
    <div ref={ref} className={joinClass('bombiix-card__footer', className)} {...rest}>
      {children}
    </div>
  );
});

const Tag = forwardRef<HTMLSpanElement, TagProps>(function Tag({ className, variant, children, ...rest }, ref) {
  const variantClass = variant ? `bombiix-card__tag--${variant}` : null;
  return (
    <span ref={ref} className={joinClass('bombiix-card__tag', variantClass, className)} {...rest}>
      {children}
    </span>
  );
});

type BombiixCardType = typeof Card & {
  Header: typeof Header;
  Actions: typeof Actions;
  Thumb: typeof Thumb;
  Body: typeof Body;
  Title: typeof Title;
  Meta: typeof Meta;
  Footer: typeof Footer;
  Tag: typeof Tag;
};

const BombiixCard = Card as BombiixCardType;
BombiixCard.Header = Header;
BombiixCard.Actions = Actions;
BombiixCard.Thumb = Thumb;
BombiixCard.Body = Body;
BombiixCard.Title = Title;
BombiixCard.Meta = Meta;
BombiixCard.Footer = Footer;
BombiixCard.Tag = Tag;

export default BombiixCard;
